import React from "react";
import {
  Box,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";

/**
 * DataTable — Chakra Table wrapped with the template's dense styling: tiny
 * uppercase headers (34px), 43px rows, ellipsis cells, subtle hover.
 * Template ref: `table`, `th`, `td`, `tbody tr:hover td`.
 *
 * Props:
 *   columns   - array of { key, label, width, align } describing the header row.
 *               `width` (e.g. "25%" / "120px") drives the fixed table layout.
 *   rows      - array of row data objects.
 *   renderRow - (row, index) => <Tr>...</Tr>. Required whenever `rows` is
 *               non-empty; caller owns cell content/actions. Defaults to a
 *               no-op so an omitted callback renders nothing instead of throwing.
 *   loading   - when true, renders skeleton rows.
 *   loadingRows - skeleton row count (default 5).
 *   emptyState  - node rendered (spanning all columns) when rows is empty.
 */
const DataTable = ({
  columns = [],
  rows = [],
  renderRow = () => null,
  loading = false,
  loadingRows = 5,
  emptyState = null,
  ...rest
}) => {
  const colCount = columns.length || 1;

  return (
    <Table
      sx={{
        tableLayout: "fixed",
        fontSize: "12px",
        "& thead th": {
          height: "34px",
          py: 0,
          px: "12px",
          fontSize: "10.5px",
          bg: "portal.soft",
          borderColor: "portal.line",
        },
        "& tbody td": {
          height: "43px",
          py: 0,
          px: "12px",
          fontWeight: 600,
          color: "#243543",
          borderColor: "#edf2f5",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        "& tbody tr:hover td": { bg: "#FBFEFE" },
      }}
      {...rest}
    >
      <Thead>
        <Tr>
          {columns.map((col, i) => (
            <Th
              key={col.key || i}
              width={col.width}
              textAlign={col.align || "left"}
            >
              {col.label}
            </Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {loading ? (
          Array.from({ length: loadingRows }).map((_, r) => (
            <Tr key={`sk-${r}`}>
              {columns.map((col, c) => (
                <Td key={col.key || c} textAlign={col.align || "left"}>
                  <Skeleton height="14px" />
                </Td>
              ))}
            </Tr>
          ))
        ) : rows.length === 0 ? (
          <Tr>
            <Td colSpan={colCount} p={0} border="none" sx={{ height: "auto !important" }}>
              <Box>{emptyState}</Box>
            </Td>
          </Tr>
        ) : (
          rows.map((row, i) => renderRow(row, i))
        )}
      </Tbody>
    </Table>
  );
};

export default DataTable;
